// White-label theme bundle (CB-12, CB-16). This directory is the ONLY place brand
// identity and design token values may be hardcoded (INF-1 / gate exemption for
// theme/). A different tenant swaps these files; nothing else in the surface changes.
//
// Typed mirror of theme.css for TS consumers. Components style through `var(--…)`
// references (`token`, `tone`, `series`, `radius`) so light/dark switching stays in
// CSS; the raw hex `palette` exists only for contexts that cannot resolve a CSS
// variable (a <canvas> fill, an SVG rendered as an image, <meta name=theme-color>).
import type { StatusTone } from '@bookingdei/i18n/status-tone';

export const brand = {
  name: 'BookingDei',
  merchant_suffix: 'Merchant',
  tagline_en: 'Run your bookings, get paid, grow',
  tagline_bn: 'আপনার বুকিং পরিচালনা করুন, পেমেন্ট নিন, এগিয়ে যান',
} as const;

// Semantic colour references (design token names, Color Tokens.md).
export const token = {
  canvas: 'var(--canvas)',
  bg: 'var(--color-bg)',
  panel2: 'var(--panel2)',
  text: 'var(--color-text)',
  muted: 'var(--muted)',
  muted2: 'var(--muted2)',
  brd: 'var(--brd)',
  hair: 'var(--hair)',
  divider: 'var(--color-divider)',
  rowHover: 'var(--row-hover)',
  accent: 'var(--color-accent)',
  accent100: 'var(--color-accent-100)',
  accent200: 'var(--color-accent-200)',
  accent600: 'var(--color-accent-600)',
  accent700: 'var(--color-accent-700)',
  accentSoft: 'var(--accent-soft)',
  onAccent: 'var(--on-accent)',
  navy: 'var(--color-navy)',
  onNavy: 'var(--on-navy)',
  ok: 'var(--c-ok)',
  warn: 'var(--c-warn)',
  info: 'var(--c-info)',
  danger: 'var(--danger)',
  dangerSoft: 'var(--danger-soft)',
  dangerStrong: 'var(--danger-strong)',
  starOn: 'var(--star-on)',
  starOff: 'var(--color-neutral-300)',
  codeInk: 'var(--code-ink)',
} as const;

// Status tone (packages/i18n status-tone, CB-15) → chip colours. `solid` is the dot
// / stroke colour of the same tone.
export const tone: Record<StatusTone, { bg: string; fg: string; solid: string }> = {
  ok: { bg: 'var(--tone-ok-bg)', fg: 'var(--tone-ok-fg)', solid: 'var(--c-ok)' },
  warn: { bg: 'var(--tone-warn-bg)', fg: 'var(--tone-warn-fg)', solid: 'var(--c-warn)' },
  info: { bg: 'var(--tone-info-bg)', fg: 'var(--tone-info-fg)', solid: 'var(--c-info)' },
  danger: { bg: 'var(--tone-danger-bg)', fg: 'var(--tone-danger-fg)', solid: 'var(--danger)' },
  neutral: { bg: 'var(--tone-neutral-bg)', fg: 'var(--tone-neutral-fg)', solid: 'var(--muted2)' },
};

// Chart series (Color Tokens.md "Charts"): series 1 is the accent.
export const series = {
  s1: 'var(--series-1)',
  s2: 'var(--series-2)',
  s3: 'var(--series-3)',
  s4: 'var(--series-4)',
  barPale: 'var(--bar-pale)',
  grid: 'var(--grid)',
  meterOff: 'var(--meter-off)',
} as const;

export const radius = {
  pill: 'var(--r-pill)',
  card: 'var(--r-card)',
  nest: 'var(--r-nest)',
  control: 'var(--r-control)',
  tag: 'var(--r-tag)',
} as const;

// Payment-method badge colours (Color Tokens.md "Payment providers" + code-only
// rocket/bank/venue), theme-independent.
export const paymentBrand: Record<string, string> = {
  bkash: 'var(--pay-bkash)',
  nagad: 'var(--pay-nagad)',
  rocket: 'var(--pay-rocket)',
  bank: 'var(--pay-bank)',
  pay_at_venue: 'var(--pay-venue)',
};

// Raw values for non-CSS contexts. Must equal theme.css (theme.test.ts checks it).
export const palette = {
  light: {
    canvas: '#E5EFFC',
    bg: '#FFFFFF',
    text: '#1B2530',
    muted: '#4F5965', // DC123: was #5B6673
    muted2: '#555F6B', // DC123: was #646F7C
    hair: '#E3ECF9',
    accent: '#0E7490',
    navy: '#0E1B2C',
    panel2: '#E5EFFC',
  },
  dark: {
    canvas: '#0A121C',
    bg: '#16232F',
    text: '#E8EEF4',
    muted: '#A8B6C4',
    muted2: '#95A5B4',
    hair: '#1E2C38',
    accent: '#7FB8CC',
    navy: '#0E1B2C',
    panel2: '#0F1B26',
  },
} as const;

// Browser chrome colour per resolved theme (<meta name="theme-color">): the page
// canvas, so the status bar blends into the app. Mirrored in index.html's pre-paint
// script, which runs before this module loads.
export const themeColor = { light: palette.light.canvas, dark: palette.dark.canvas } as const;

// A printable QR is always dark modules on white, whatever the UI theme.
export const qrPalette = { paper: '#FFFFFF', ink: palette.light.navy } as const;
